import React from "react";

/**
 * ServerTopology — /server 페이지 상단의 서버 간 구조도(데이터 흐름).
 *
 * 2026-06 SSH 점검으로 확인한 각 서버의 실제 역할/포트를 기반으로 한 논리 흐름도이다.
 * 화살표는 "어떤 데이터가 어디로 흐르는가"를 나타내는 논리 연결이며, SVG 는 모두
 * 디자인 시스템 CSS 변수(--color-*)로 칠해 3개 테마 전환에 자동 대응한다.
 *
 * 노드 분류:
 *   - cloud  : 클라우드 서버 (brand 액센트)
 *   - local  : 개인 로컬 머신 (중립 테두리)
 *   - idle   : 유휴 서버 (점선 · muted)
 *   - ext    : 외부 시스템 (거래소·Discord·사용자, 점선)
 */

const STROKE = "var(--color-surface-border)";
const STROKE_LIGHT = "var(--color-surface-border-light)";
const FILL_CARD = "var(--color-surface-card)";
const FILL_ALT = "var(--color-surface-card-alt)";
const FILL_ELEV = "var(--color-surface-elevated)";
const BRAND = "var(--color-brand)";
const C_TITLE = "var(--color-content)";
const C_SUB = "var(--color-content-muted)";
const C_SEC = "var(--color-content-secondary)";

const VARIANT = {
    cloud: { fill: FILL_CARD, stroke: BRAND, dash: null, accent: BRAND },
    local: { fill: FILL_CARD, stroke: STROKE, dash: null, accent: STROKE },
    idle: { fill: FILL_ALT, stroke: STROKE_LIGHT, dash: "4 3", accent: STROKE_LIGHT },
    ext: { fill: FILL_ELEV, stroke: STROKE_LIGHT, dash: "3 3", accent: STROKE_LIGHT },
};

/** 라운드 사각형 노드 1개. */
function Node({ x, y, w, h, variant = "local", title, sub, port }) {
    const v = VARIANT[variant] || VARIANT.local;
    return (
        <g>
            <rect
                x={x} y={y} width={w} height={h} rx="8"
                fill={v.fill} stroke={v.stroke} strokeWidth="1.5"
                strokeDasharray={v.dash || undefined}
            />
            {/* 좌측 카테고리 액센트 바 */}
            <rect x={x} y={y} width="3" height={h} rx="1.5" fill={v.accent} />
            <text x={x + 14} y={y + (sub ? 22 : h / 2 + 4)} fill={C_TITLE} fontSize="13" fontWeight="600">
                {title}
            </text>
            {sub && (
                <text x={x + 14} y={y + 39} fill={C_SUB} fontSize="12">
                    {sub}
                </text>
            )}
            {port && (
                <text x={x + w - 12} y={y + 18} fill={C_SEC} fontSize="12" textAnchor="end" fontFamily="ui-monospace, monospace">
                    {port}
                </text>
            )}
        </g>
    );
}

/** 화살표 엣지 + 옵션 라벨. */
function Edge({ x1, y1, x2, y2, label, dashed, curve }) {
    const d = curve
        ? `M ${x1} ${y1} C ${x1 + (x2 - x1) * 0.5} ${y1}, ${x1 + (x2 - x1) * 0.5} ${y2}, ${x2} ${y2}`
        : `M ${x1} ${y1} L ${x2} ${y2}`;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    return (
        <g>
            <path
                d={d} fill="none" stroke={BRAND} strokeWidth="1.4"
                strokeOpacity="0.55" strokeDasharray={dashed ? "5 4" : undefined}
                markerEnd="url(#topo-arrow)"
            />
            {label && (
                <text x={mx} y={my - 4} fill={C_SUB} fontSize="12" textAnchor="middle">
                    <tspan dx="0" style={{ paintOrder: "stroke" }}>{label}</tspan>
                </text>
            )}
        </g>
    );
}

export default function ServerTopology() {
    return (
        <div className="bg-surface-card border border-surface-border rounded-lg p-4 sm:p-5 overflow-x-auto">
            <div className="flex items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-semibold text-content">서버 구조도 · 데이터 흐름</h2>
                <Legend />
            </div>
            <svg
                viewBox="0 0 940 470"
                className="w-full min-w-[760px]"
                role="img"
                aria-label="서버 간 데이터 흐름 구조도"
            >
                <defs>
                    <marker
                        id="topo-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="6" markerHeight="6" orient="auto-start-reverse"
                    >
                        <path d="M 0 0 L 10 5 L 0 10 z" fill={BRAND} fillOpacity="0.7" />
                    </marker>
                </defs>

                {/* ── Edges (노드 뒤에 깔림) ── */}
                {/* 거래소 → 수집/실행/알림/모니터링 */}
                <Edge x1={210} y1={120} x2={330} y2={92} label="시세·체결" />
                <Edge x1={210} y1={150} x2={330} y2={205} label="주문" />
                <Edge x1={210} y1={180} x2={330} y2={320} label="공지·지갑" />
                {/* NUC → MySQL DB */}
                <Edge x1={510} y1={86} x2={650} y2={86} label="수집 저장" />
                {/* MySQL DB → Dashboard EC2 */}
                <Edge x1={730} y1={116} x2={620} y2={210} label="집계 조회" curve />
                {/* ExchangeNotification → Dashboard EC2 (지갑상태) */}
                <Edge x1={510} y1={320} x2={540} y2={250} label="지갑상태" />
                {/* ExchangeNotification → Discord */}
                <Edge x1={420} y1={350} x2={420} y2={398} label="알림" />
                {/* Dashboard EC2 → 사용자 */}
                <Edge x1={720} y1={230} x2={800} y2={230} label="React" />

                {/* ── Nodes ── */}
                <Node x={60} y={100} w={150} h={100} variant="ext" title="거래소 API" sub="Binance·Upbit" />
                <text x={135} y={178} fill={C_SUB} fontSize="12" textAnchor="middle">Bithumb·Korbit</text>

                <Node x={330} y={60} w={180} h={58} variant="local" title="Backtest NUC Pro" sub="수집 collector + paper" />
                <Node x={330} y={178} w={180} h={58} variant="cloud" title="Strategy EC2" sub="live 트레이딩" />
                <Node x={330} y={296} w={180} h={58} variant="cloud" title="ExchangeNotification" sub="공지·지갑 알림" port=":8000" />

                <Node x={650} y={58} w={180} h={58} variant="cloud" title="MySQL DB" sub="Oracle · 공용 데이터" port=":3306" />
                <Node x={540} y={196} w={180} h={68} variant="cloud" title="Dashboard EC2" sub="go-server 모니터링 허브" port=":8001" />
                <Node x={800} y={200} w={120} h={58} variant="ext" title="사용자" sub="React UI" />
                <Node x={330} y={398} w={180} h={48} variant="ext" title="Discord" sub="점검·청산 알림" />

                {/* 독립 노드: 리서치 / 유휴 */}
                <Node x={540} y={296} w={180} h={58} variant="local" title="Mac mini (M4)" sub="리서치 · 오프라인 분석" />
                <Node x={650} y={372} w={130} h={44} variant="idle" title="유휴 #1" sub="liq_backtest 예정" />
                <Node x={790} y={372} w={130} h={44} variant="idle" title="유휴 #2" sub="liq_paper 예정" />
            </svg>
            <p className="text-xs text-content-muted mt-3 leading-relaxed">
                화살표는 논리적 데이터 흐름입니다. Dashboard EC2 는 거래소 잔고를 직접 조회하고
                MySQL·ExchangeNotification 데이터를 집계해 대시보드로 제공합니다.
            </p>
        </div>
    );
}

function Legend() {
    const items = [
        { label: "클라우드", cls: "bg-brand" },
        { label: "로컬", cls: "bg-surface-border-light" },
        { label: "유휴", cls: "bg-surface-card-alt border border-surface-border-light" },
    ];
    return (
        <div className="hidden sm:flex items-center gap-3">
            {items.map((it) => (
                <span key={it.label} className="inline-flex items-center gap-1.5 text-xs text-content-muted">
                    <span className={`w-2.5 h-2.5 rounded-sm ${it.cls}`} aria-hidden="true" />
                    {it.label}
                </span>
            ))}
        </div>
    );
}
